import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useGroup } from '../contexts/GroupContext';

/**
 * Custom hook that extends useQuery to include the selected group in the query key.
 * This ensures proper caching when switching between groups.
 * 
 * @param baseQueryKey The base query key (without the group)
 * @param queryFn The query function to execute
 * @param options Additional useQuery options
 * @returns The useQuery result
 */
export function useGroupQuery<TData, TError = unknown>(
  baseQueryKey: unknown[],
  queryFn: () => Promise<TData>,
  options?: Omit<UseQueryOptions<TData, TError, TData>, 'queryKey' | 'queryFn'>
): UseQueryResult<TData, TError> {
  const { selectedGroup, isLoading: isGroupLoading } = useGroup();
  const { enabled: enabledOption, ...restOptions } = options ?? {};

  // Include the selected group in the query key
  const queryKey = [...baseQueryKey, selectedGroup];

  return useQuery({
    queryKey,
    queryFn,
    // Merge caller `enabled` with group readiness; do not let ...options override this
    enabled:
      !!selectedGroup &&
      !isGroupLoading &&
      (enabledOption !== false),
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: (previousData) => previousData,
    ...restOptions,
  });
} 